import React from 'react';

import camelcase from 'camelcase';
import { FormattedMessage } from 'react-intl';
import { all, call, put, takeEvery } from 'redux-saga/effects';

import * as actions from '@actions/savedFilingPeriod';
import {
  createSavedFilingPeriod,
  getSavedFilingFilePeriod,
  getSavedFilingPeriodById,
  listSavedFilingPeriodFiles,
} from '@api/savedFilingPeriod';
import { listSpreadsheets } from '@api/spreadsheet';
import types from '@constants/action-types';
import { fileListModel } from '@models/FileList/fileListModel';
import { handleRequest } from '@sagas/request';
import { deleteSavedFilingPeriodFileSaga } from '@sagas/savedFilingPeriod/deleteSavedFilingPeriodFileSaga';
import { deleteSingleSpreadsheetSaga } from '@sagas/savedFilingPeriod/deleteSingleSpreadsheetSaga';
import { handleUpdateFilingByIdRequest } from '@sagas/savedFilingPeriod/handleUpdateFilingByIdRequest';
import { toastError } from '@store/notifications';

export const handleGetSavedFilingPeriodFileRequest = function* ({ payload }) {
  try {
    const response = yield call(getSavedFilingFilePeriod, payload);
    if (response?.isAxiosError)
      return yield put(actions.getSavedFilingPeriodFileFailure(response?.message));
    yield put(actions.getSavedFilingPeriodFileSuccess(response));
  } catch (e) {
    yield put(actions.getSavedFilingPeriodFileFailure(e));
  }
};

export const handleCreateSavedFilingPeriodRequest = function* ({ payload }) {
  try {
    const response = yield call(createSavedFilingPeriod, payload);
    yield handleGetSavedFilingPeriodFileRequest({ payload: { id: response?.id } });

    if (payload.onSuccess) {
      yield call(payload.onSuccess);
    }
  } catch (errors) {
    const { nonFieldErrors } = errors?.data || {};
    yield put(
      toastError({
        id: 'create-sfp-error',
        message: nonFieldErrors || (
          <FormattedMessage
            defaultMessage="An unexpected error occurred, please try again."
            id="global.notification.api-error-default"
          />
        ),
      })
    );
    yield put(actions.createSavedFilingPeriodFailure(errors));
  }
};

export const handleListSavedFilingPeriodFilesRequest = function* ({ payload = {} }) {
  try {
    const category = payload.filesMapGroup || payload.categories?.[0];
    const response = yield call(listSavedFilingPeriodFiles, payload);

    if (response.isAxiosError)
      return yield put(actions.listSavedFilingPeriodFilesFailure(response.message));
    yield put(
      actions.listSavedFilingPeriodFilesSuccess({
        files: fileListModel(response.results),
        category: camelcase(category),
      })
    );
  } catch (e) {
    yield put(actions.listSavedFilingPeriodFilesFailure(e));
  }
};

export const handleGetSavedFilingPeriodByIdRequest = function* ({ payload }) {
  try {
    const response = yield call(getSavedFilingPeriodById, payload);
    if (response.isAxiosError)
      return yield put(actions.getSavedFilingPeriodByIdFailure(response.message));
    yield put(actions.getSavedFilingPeriodByIdSuccess(response));
  } catch (e) {
    yield put(actions.getSavedFilingPeriodByIdFailure(e));
  }
};

export const handleListSavedFilingPeriodSpreadsheetsRequest = function* ({ payload }) {
  try {
    const response = yield call(listSpreadsheets, payload);
    if (response.isAxiosError)
      return yield put(actions.listSavedFilingPeriodSpreadsheetsFailure(response.message));
    yield put(actions.listSavedFilingPeriodSpreadsheetsSuccess(response));
  } catch (e) {
    yield put(actions.listSavedFilingPeriodSpreadsheetsFailure(e));
  }
};

export default all([
  takeEvery(types.CREATE_SAVED_FILING_PERIOD_REQUEST, handleCreateSavedFilingPeriodRequest),
  takeEvery(types.GET_SAVED_FILING_PERIOD_FILE_REQUEST, handleGetSavedFilingPeriodFileRequest),
  takeEvery(types.DELETE_SAVED_FILING_PERIOD_FILE_REQUEST, deleteSavedFilingPeriodFileSaga),
  takeEvery(types.LIST_SAVED_FILING_PERIOD_FILES_REQUEST, handleListSavedFilingPeriodFilesRequest),
  takeEvery(types.GET_SAVED_FILING_PERIOD_BY_ID_REQUEST, handleGetSavedFilingPeriodByIdRequest),
  takeEvery(types.UPDATE_SAVED_FILING_PERIOD_BY_ID_REQUEST, handleUpdateFilingByIdRequest),
  takeEvery(
    types.LIST_SAVED_FILING_PERIOD_JSON_REQUEST,
    handleListSavedFilingPeriodSpreadsheetsRequest
  ),
  takeEvery(types.DELETE_SAVED_FILING_PERIOD_JSON_REQUEST, deleteSingleSpreadsheetSaga),
  takeEvery(types.UPDATE_FILING_OUTCOME_REQUEST, handleRequest),
  takeEvery(types.UPDATE_PAYMENT_DETAILS_REQUEST, handleRequest),
]);
